import { uploadMeta } from "~/utils/files-cdn"
import { useQueryClient } from "@tanstack/react-query"
import { NotificationInstance } from "antd/es/notification/interface"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import {
  AttachedExistingFile,
  attachExistingFiles as attachExistingFilesApi,
  deleteScopedFiles,
  getPresignedUrls,
  getScopedFilesStatus,
  notifyFilesUploaded,
  PresignedUrl,
  ScopedFile,
  uploadFileToS3
} from "~/api/agent-uploads"
import {
  normalizeFileId,
  type ISelectedFile
} from "~/components/select-files-picker/types"
import { useScopedFilesStatus } from "~/hooks/useScopedFilesStatus"
import { useTranscriptionStatus } from "~/hooks/use-transcription-status"
import useGlobalStore from "~/store/global"
import { ITokenInfo } from "~/types/global"

export type AgentFilePhase = "UPLOADING" | "PROCESSING" | "COMPLETED" | "FAILED"

// Consecutive `scoped_files_status` polls reporting a non-null `error` (while
// < 100%) before we treat it as a terminal failure. >1 so a single-poll
// flicker mid-pipeline doesn't fail a file that later completes; small enough
// to surface real failures (e.g. "Max retries reached") in seconds rather
// than waiting for the multi-minute processing timeout.
const ERROR_FAIL_POLLS = 3

export interface AgentFile {
  localId: string
  name: string
  fileId?: string
  s3Path?: string
  phase: AgentFilePhase
  uploadPct: number
  processingPct: number
  error?: string
  /**
   * Consecutive `scoped_files_status` polls that reported a non-null `error`
   * while still < 100%. The backend's `error` can flicker mid-pipeline, so we
   * only flip to FAILED once it PERSISTS (see the status reducer), not on the
   * first occurrence. Reset to 0 whenever the error clears or the file
   * completes.
   */
  errorPolls?: number
  /**
   * Discriminator for where the file came from.
   * "kb" identifies Knowledge Library picks — the BE resolves their s3_path
   * from the KB table rather than from rfp_session_files.
   * Undefined for ordinary session-scoped uploads.
   */
  source?: "my_files" | "shared" | "kb"
  /**
   * Transcription state for media files injected from the picker.
   * Undefined for ordinary file uploads. Set to "pending" when a picker entry
   * has pending transcription; transitions to "ready" or "failed" via polling.
   */
  transcription_status?: "pending" | "ready" | "failed"
  // Bare base64 user-scoped file_id captured at attach-seed time, before
  // the BE response sets `fileId` to the composite. Used by the picker's
  // currentlyAttached dedup so a re-pick of an in-flight file (still
  // PROCESSING, fileId=undefined) is recognised as already-attached.
  pickFileId?: string
  // Original File object kept for retry-from-scratch when S3 upload fails.
  // Cleared once the file transitions to COMPLETED.
  rawFile?: File
}

export interface UseAgentFileUploadOptions {
  /**
   * Current session ID that scopes the files. When an attachment endpoint is
   * called, this is the value used in the URL. For RFP this is the sub-session
   * ID; for KOS / Ask OS this is the agent session ID.
   */
  sessionId: string
  /**
   * Called lazily the first time a file upload needs a session. If `sessionId`
   * is already set this is not invoked.
   */
  ensureSessionId?: () => Promise<string>
  /**
   * Extra sessionId for RFP's CHAT_CONTEXT presigned URL body (the `sessionId`
   * field on the wire). Empty string for non-RFP agents.
   */
  parentSessionId?: string
  /** RFP-specific query param for polling + notify. Others should omit. */
  showMyFilesInRfpResponse?: boolean
  maxFiles: number
  maxFileSizeMB: number
  acceptedExtensions: string[]
  notificationApi: NotificationInstance
  /**
   * Names of files already attached to prior messages in this chat.
   * Used for dedup — scoped_files_status only reports in-flight uploads,
   * so history must be supplied by the caller.
   */
  historyFileNames?: string[]
  /**
   * Per-file processing timeout in ms. Files stuck in PROCESSING longer than
   * this are marked FAILED. Default 5 minutes.
   */
  processingTimeoutMs?: number
}

const uid = () =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`

export function useAgentFileUpload(options: UseAgentFileUploadOptions) {
  const {
    sessionId,
    ensureSessionId,
    parentSessionId,
    showMyFilesInRfpResponse,
    maxFiles,
    maxFileSizeMB,
    acceptedExtensions,
    notificationApi,
    historyFileNames,
    processingTimeoutMs = 5 * 60 * 1000
  } = options

  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  const [files, setFiles] = useState<AgentFile[]>([])
  const [resolvedSessionId, setResolvedSessionId] = useState(sessionId)
  // True while we're awaiting session creation / status refetch but haven't
  // yet added the chips to `files`. Drives the "Initializing session…"
  // placeholder so the user gets immediate feedback on click.
  const [isInitializing, setIsInitializing] = useState(false)

  useEffect(() => {
    if (sessionId && sessionId !== resolvedSessionId) {
      setResolvedSessionId(sessionId)
    }
  }, [sessionId, resolvedSessionId])

  const abortControllers = useRef(new Map<string, AbortController>())
  const processingStartedAt = useRef(new Map<string, number>())
  // Synchronous in-flight tracker keyed by file name. Mutated before any
  // await in `addFiles` so a second concurrent call (double-click, drag+drop
  // racing the input change, React 18 strict-mode re-run) sees the file
  // already in flight and skips it. React state can't guard this — setState
  // is async, so two callers in the same tick capture the same stale snapshot.
  const inFlightNames = useRef(new Set<string>())

  const hasActiveFiles = useMemo(
    () =>
      files.some((f) => f.phase === "UPLOADING" || f.phase === "PROCESSING"),
    [files]
  )

  const hasProcessingFiles = useMemo(
    () => files.some((f) => f.phase === "PROCESSING"),
    [files]
  )

  // Always enabled when we have a session so we know the authoritative
  // file count for cap enforcement. Polling interval is gated downstream
  // by refetchInterval — no active files → one-shot fetch, no repeat.
  const { data: statusData, refetch: refetchStatus } = useScopedFilesStatus({
    sessionId: resolvedSessionId,
    enabled: !!resolvedSessionId,
    showMyFilesInRfpResponse
  })

  // When a new batch transitions to PROCESSING, kick polling back on.
  useEffect(() => {
    if (hasProcessingFiles) refetchStatus()
  }, [hasProcessingFiles, refetchStatus])

  // Poll transcription status for picker-injected media files.
  const pendingTranscriptionIds = useMemo(
    () =>
      files
        .filter((f) => f.transcription_status === "pending" && f.fileId)
        .map((f) => f.fileId as string),
    [files]
  )
  const { data: transcriptionData } = useTranscriptionStatus(
    pendingTranscriptionIds,
    pendingTranscriptionIds.length > 0
  )

  useEffect(() => {
    const remote = transcriptionData?.files
    if (!remote || remote.length === 0) return
    setFiles((prev) => {
      let changed = false
      const next = prev.map((f) => {
        if (!f.fileId || f.transcription_status !== "pending") return f
        const match = remote.find(
          (r: {
            id?: string
            file_id?: string
            transcription_status?: string
          }) => String(r.id ?? r.file_id) === String(f.fileId)
        )
        if (!match) return f
        const status = match.transcription_status as
          | "pending"
          | "ready"
          | "failed"
          | undefined
        if (status === "ready" || status === "failed") {
          changed = true
          return {
            ...f,
            transcription_status: status,
            phase:
              status === "ready"
                ? ("COMPLETED" as AgentFilePhase)
                : ("FAILED" as AgentFilePhase),
            processingPct: status === "ready" ? 100 : f.processingPct,
            error:
              status === "failed"
                ? t("rfp_os.label_processing_timed_out")
                : undefined
          }
        }
        return f
      })
      return changed ? next : prev
    })
  }, [transcriptionData, t])

  useEffect(() => {
    const remote = statusData?.data?.files
    if (!remote || remote.length === 0) return

    setFiles((prev) => {
      let changed = false
      const next = prev.map((f) => {
        if (!f.fileId) return f
        // COMPLETED is terminal. FAILED can recover — backend may have
        // finished processing after a transient error the UI caught mid-flight.
        if (f.phase === "COMPLETED") return f

        const match = remote.find((r) => String(r.id) === String(f.fileId))
        if (!match) return f

        const done = match.percentage_completion === 100

        // `percentage_completion === 100` is authoritative for success. The
        // backend's `error` can flicker during the multi-stage pipeline
        // (parse → translate → redact → ingest) — a SINGLE non-null `error`
        // doesn't mean terminal failure (it often clears and reaches 100%).
        // So we don't fail on the first error; we fail only when it PERSISTS
        // across `ERROR_FAIL_POLLS` consecutive polls (e.g. "Max retries
        // reached"), which is far faster than waiting for the 5-min timeout
        // while still ignoring transient flicker.
        const remoteErr =
          typeof match.error === "string" ? match.error.trim() : ""
        const errStreak = !done && remoteErr ? (f.errorPolls ?? 0) + 1 : 0
        const terminalFail = errStreak >= ERROR_FAIL_POLLS

        const newPhase: AgentFilePhase = done
          ? "COMPLETED"
          : terminalFail
            ? "FAILED"
            : f.phase

        const newPct = Math.max(
          f.processingPct,
          match.percentage_completion ?? 0
        )
        const newErrorPolls = terminalFail ? 0 : errStreak
        if (
          newPhase !== f.phase ||
          newPct !== f.processingPct ||
          newErrorPolls !== (f.errorPolls ?? 0)
        ) {
          changed = true
          return {
            ...f,
            phase: newPhase,
            processingPct: newPct,
            errorPolls: newErrorPolls,
            error:
              newPhase === "COMPLETED"
                ? undefined
                : newPhase === "FAILED"
                  ? remoteErr || f.error || t("rfp_os.label_file_status_failed")
                  : f.error,
            rawFile: newPhase === "COMPLETED" ? undefined : f.rawFile
          }
        }
        return f
      })
      return changed ? next : prev
    })
  }, [statusData])

  // Per-file processing timeout
  useEffect(() => {
    if (!hasActiveFiles) return
    const interval = setInterval(() => {
      setFiles((prev) => {
        let changed = false
        const next = prev.map((f) => {
          if (f.phase !== "PROCESSING") return f
          const startedAt = processingStartedAt.current.get(f.localId)
          if (!startedAt) return f
          if (Date.now() - startedAt <= processingTimeoutMs) return f
          changed = true
          return {
            ...f,
            phase: "FAILED" as AgentFilePhase,
            error: t("rfp_os.label_processing_timed_out")
          }
        })
        return changed ? next : prev
      })
    }, 30_000)
    return () => clearInterval(interval)
  }, [hasActiveFiles, processingTimeoutMs, t])

  const validateFiles = useCallback(
    (incoming: File[], serverFiles: ScopedFile[]): File[] => {
      if (!incoming.length) return []
      const maxBytes = maxFileSizeMB * 1024 * 1024

      // Per-batch cap only (no cumulative session limit) — reject the
      // whole batch with one toast if it exceeds maxFiles.
      if (incoming.length > maxFiles) {
        notificationApi.warning({
          message: t("rfp_os.label_too_many_files"),
          description: t("rfp_os.label_upload_max_files", {
            defaultValue: `Please select up to ${maxFiles} files.`,
            max: maxFiles
          }),
          placement: "topRight"
        })
        return []
      }

      // Collect ALL per-file rejections (extension, size, duplicate) and
      // emit ONE toast at the end with a combined summary.
      const supported: File[] = []
      const unsupportedExt: File[] = []
      const oversized: File[] = []
      for (const file of incoming) {
        const ext = "." + (file.name.split(".").pop()?.toLowerCase() ?? "")
        if (!acceptedExtensions.includes(ext)) {
          unsupportedExt.push(file)
          continue
        }
        if (file.size > maxBytes) {
          oversized.push(file)
          continue
        }
        supported.push(file)
      }

      const serverNames = serverFiles
        .map((f) => f.name || f.s3_path?.split("/").pop() || "")
        .filter(Boolean)
      const existingNames = new Set([
        ...files.map((f) => f.name),
        ...serverNames,
        ...(historyFileNames ?? [])
      ])
      const duplicates = supported.filter((f) => existingNames.has(f.name))
      const deduped = supported.filter((f) => !existingNames.has(f.name))

      const issues: string[] = []
      if (unsupportedExt.length) {
        issues.push(
          `${unsupportedExt.length} unsupported: ${unsupportedExt.map((f) => f.name).join(", ")}`
        )
      }
      if (oversized.length) {
        issues.push(
          `${oversized.length} over ${maxFileSizeMB} MB: ${oversized.map((f) => f.name).join(", ")}`
        )
      }
      if (duplicates.length) {
        issues.push(
          `${duplicates.length} already attached: ${duplicates.map((f) => f.name).join(", ")}`
        )
      }
      if (issues.length) {
        notificationApi.warning({
          message: t("rfp_os.label_some_files_skipped", {
            defaultValue: "Some files skipped"
          }),
          description: issues.join(" | "),
          placement: "topRight"
        })
      }
      return deduped
    },
    [
      acceptedExtensions,
      files,
      historyFileNames,
      maxFileSizeMB,
      maxFiles,
      notificationApi,
      t
    ]
  )

  const addFiles = useCallback(
    async (incoming: File[]): Promise<void> => {
      if (!tokenInfo) return

      // Synchronous concurrency guard: drop names already mid-upload from a
      // prior call still in flight, then claim the rest before any await.
      // Pairs with cleanup in finally + clear/removeFile.
      const claimed: string[] = []
      const filtered: File[] = []
      for (const f of incoming) {
        if (inFlightNames.current.has(f.name)) continue
        inFlightNames.current.add(f.name)
        claimed.push(f.name)
        filtered.push(f)
      }
      if (filtered.length === 0) return

      setIsInitializing(true)
      try {
        let sid = resolvedSessionId
        if (!sid && ensureSessionId) {
          try {
            sid = await ensureSessionId()
            if (sid) setResolvedSessionId(sid)
          } catch {
            notificationApi.error({
              message: t("rfp_os.label_upload_failed"),
              description: t("rfp_os.label_unable_to_start_session_upload"),
              placement: "topRight"
            })
            return
          }
        }
        if (!sid) {
          notificationApi.error({
            message: t("rfp_os.label_upload_failed"),
            description: t("rfp_os.label_no_session_available"),
            placement: "topRight"
          })
          return
        }

        // Pull fresh server-side state with the resolved sid directly —
        // refetchStatus() would replay the hook's stale "" sessionId and
        // hit /session//scoped_files_status (404).
        let serverFiles: ScopedFile[] = statusData?.data?.files ?? []
        try {
          const fresh = await getScopedFilesStatus({
            tokenInfo: tokenInfo as ITokenInfo,
            sessionId: sid,
            showMyFilesInRfpResponse
          })
          const freshList = (fresh.data as { data?: { files?: ScopedFile[] } })
            ?.data?.files
          if (freshList) serverFiles = freshList
        } catch {
          // proceed with whatever we already have
        }

        const valid = validateFiles(filtered, serverFiles)
        if (!valid.length) return

        const batch: AgentFile[] = valid.map((file) => ({
          localId: uid(),
          name: file.name,
          phase: "UPLOADING",
          uploadPct: 0,
          processingPct: 0,
          rawFile: file
        }))
        setFiles((prev) => [...prev, ...batch])
        // Chips are now visible with "Uploading" — drop the initializing
        // placeholder so we don't double up.
        setIsInitializing(false)

        try {
          const presignedResp = await getPresignedUrls({
            tokenInfo: tokenInfo as ITokenInfo,
            sessionId: sid,
            parentSessionId,
            files: valid.map((f) => ({ name: f.name, ...uploadMeta(f) }))
          })
          const presignedUrls: PresignedUrl[] =
            presignedResp.data?.data?.presignedUrls ||
            presignedResp.data?.presignedUrls ||
            []
          if (!presignedUrls.length) {
            throw new Error(t("rfp_os.label_no_presigned_urls_returned"))
          }

          const filesPath: string[] = []
          const orderedRecords: {
            record: AgentFile
            file: File
            signed: PresignedUrl
          }[] = []
          for (const signed of presignedUrls) {
            const file = valid.find((f) => f.name === signed.key)
            const record = batch.find((r) => r.name === signed.key)
            if (!file || !record) continue
            orderedRecords.push({ record, file, signed })
          }

          await Promise.all(
            orderedRecords.map(async ({ record, file, signed }) => {
              const controller = new AbortController()
              abortControllers.current.set(record.localId, controller)
              try {
                await uploadFileToS3({
                  url: signed.url,
                  file,
                  signal: controller.signal,
                  onProgress: (pct) => {
                    setFiles((prev) =>
                      prev.map((f) =>
                        f.localId === record.localId
                          ? { ...f, uploadPct: pct }
                          : f
                      )
                    )
                  }
                })
                filesPath.push(signed.s3path)
                setFiles((prev) =>
                  prev.map((f) =>
                    f.localId === record.localId
                      ? { ...f, uploadPct: 100, s3Path: signed.s3path }
                      : f
                  )
                )
              } finally {
                abortControllers.current.delete(record.localId)
              }
            })
          )

          if (!filesPath.length) {
            throw new Error(t("rfp_os.label_all_s3_uploads_failed"))
          }

          const notifyRes = await notifyFilesUploaded({
            tokenInfo: tokenInfo as ITokenInfo,
            sessionId: sid,
            filesPath,
            showMyFilesInRfpResponse
          })

          const notifyFiles: { id: string; s3_path?: string }[] =
            notifyRes.data?.data?.files || notifyRes.data?.files || []

          const now = Date.now()
          setFiles((prev) =>
            prev.map((f) => {
              if (!batch.some((b) => b.localId === f.localId)) return f
              const match =
                notifyFiles.find((n) => n.s3_path && n.s3_path === f.s3Path) ||
                notifyFiles[
                  orderedRecords.findIndex(
                    (o) => o.record.localId === f.localId
                  )
                ]
              if (!match) return f
              processingStartedAt.current.set(f.localId, now)
              return {
                ...f,
                fileId: String(match.id),
                phase: "PROCESSING",
                processingPct: 0
              }
            })
          )
        } catch (err: any) {
          const message =
            err?.response?.data?.error?.message ||
            err?.response?.data?.message ||
            err?.message ||
            t("rfp_os.label_unable_to_upload_files_try_again")
          notificationApi.error({
            message: t("rfp_os.label_upload_failed"),
            description: message,
            placement: "topRight",
            duration: 8
          })
          setFiles((prev) =>
            prev.map((f) =>
              batch.some((b) => b.localId === f.localId) &&
              f.phase !== "COMPLETED"
                ? { ...f, phase: "FAILED", error: message }
                : f
            )
          )
        }
      } finally {
        for (const name of claimed) inFlightNames.current.delete(name)
        setIsInitializing(false)
      }
    },
    [
      ensureSessionId,
      notificationApi,
      parentSessionId,
      refetchStatus,
      resolvedSessionId,
      showMyFilesInRfpResponse,
      statusData,
      tokenInfo,
      validateFiles,
      t
    ]
  )

  const removeFile = useCallback(
    async (localId: string): Promise<void> => {
      const target = files.find((f) => f.localId === localId)
      if (!target) return

      const controller = abortControllers.current.get(localId)
      if (controller) controller.abort()
      abortControllers.current.delete(localId)
      processingStartedAt.current.delete(localId)

      setFiles((prev) => prev.filter((f) => f.localId !== localId))

      if (target.fileId && resolvedSessionId && tokenInfo) {
        try {
          await deleteScopedFiles({
            tokenInfo: tokenInfo as ITokenInfo,
            sessionId: resolvedSessionId,
            fileIds: [target.fileId]
          })
          refetchStatus()
        } catch {
          // Chip already removed locally above (setFiles filter ran before
          // the API call). BE errors here are eventual-consistency: DB
          // marked is_active=false but ES cleanup may have failed (500),
          // or row already gone from a prior delete (400 No valid files).
          // Either way the user's intent is met — suppress the red toast.
        }
      }
    },
    [files, notificationApi, refetchStatus, resolvedSessionId, tokenInfo, t]
  )

  const retryFile = useCallback(
    async (localId: string): Promise<void> => {
      const target = files.find((f) => f.localId === localId)
      if (
        !target ||
        target.phase !== "FAILED" ||
        !resolvedSessionId ||
        !tokenInfo
      )
        return
      // Need SOMETHING to retry from: a local rawFile to re-upload (Path A),
      // an s3Path to re-notify (Path B), or — for a library file attached by
      // reference — its user-scoped file_id to re-attach (Path C).
      if (!target.s3Path && !target.rawFile && !target.pickFileId) return

      const fail = (message: string) => {
        setFiles((prev) =>
          prev.map((f) =>
            f.localId === localId
              ? {
                  ...f,
                  phase: "FAILED" as AgentFilePhase,
                  error: message
                }
              : f
          )
        )
      }

      try {
        // Path A: re-do S3 upload + notify (S3 upload originally failed)
        if (!target.s3Path && target.rawFile) {
          const file = target.rawFile
          setFiles((prev) =>
            prev.map((f) =>
              f.localId === localId
                ? {
                    ...f,
                    phase: "UPLOADING" as AgentFilePhase,
                    uploadPct: 0,
                    processingPct: 0,
                    error: undefined
                  }
                : f
            )
          )

          const presignedResp = await getPresignedUrls({
            tokenInfo: tokenInfo as ITokenInfo,
            sessionId: resolvedSessionId,
            parentSessionId,
            files: [{ name: file.name, ...uploadMeta(file) }]
          })
          const presignedUrls: PresignedUrl[] =
            presignedResp.data?.data?.presignedUrls ||
            presignedResp.data?.presignedUrls ||
            []
          const signed = presignedUrls.find((p) => p.key === file.name)
          if (!signed) throw new Error("No presigned URL returned")

          const controller = new AbortController()
          abortControllers.current.set(localId, controller)
          try {
            await uploadFileToS3({
              url: signed.url,
              file,
              signal: controller.signal,
              onProgress: (pct) => {
                setFiles((prev) =>
                  prev.map((f) =>
                    f.localId === localId ? { ...f, uploadPct: pct } : f
                  )
                )
              }
            })
          } finally {
            abortControllers.current.delete(localId)
          }

          setFiles((prev) =>
            prev.map((f) =>
              f.localId === localId
                ? { ...f, uploadPct: 100, s3Path: signed.s3path }
                : f
            )
          )

          const notifyRes = await notifyFilesUploaded({
            tokenInfo: tokenInfo as ITokenInfo,
            sessionId: resolvedSessionId,
            filesPath: [signed.s3path],
            showMyFilesInRfpResponse
          })
          const notifyFile = (notifyRes.data?.data?.files ||
            notifyRes.data?.files ||
            [])[0]
          processingStartedAt.current.set(localId, Date.now())
          setFiles((prev) =>
            prev.map((f) =>
              f.localId === localId
                ? {
                    ...f,
                    phase: "PROCESSING" as AgentFilePhase,
                    processingPct: 0,
                    fileId: notifyFile?.id ? String(notifyFile.id) : f.fileId
                  }
                : f
            )
          )
          refetchStatus()
          return
        }

        // Path C: re-attach a library file by its user-scoped file_id. It was
        // attached by reference (no rawFile to re-upload, and the picker
        // carried no s3Path to re-notify), so re-run the attach to re-trigger
        // the CHAT_CONTEXT ingestion for this session. pickFileId is the bare
        // user-scoped id the attach API expects (prefix stripped on seed).
        if (!target.s3Path && target.pickFileId) {
          setFiles((prev) =>
            prev.map((f) =>
              f.localId === localId
                ? {
                    ...f,
                    phase: "PROCESSING" as AgentFilePhase,
                    processingPct: 0,
                    errorPolls: 0,
                    error: undefined
                  }
                : f
            )
          )
          processingStartedAt.current.set(localId, Date.now())
          const resp = await attachExistingFilesApi({
            tokenInfo: tokenInfo as ITokenInfo,
            sessionId: resolvedSessionId,
            fileIds: [target.pickFileId]
          })
          const returned: AttachedExistingFile[] =
            resp?.data?.data?.files || resp?.data?.files || []
          const match = returned.find(
            (r) => normalizeFileId(r.file_id) === target.pickFileId
          )
          if (match?.id) {
            setFiles((prev) =>
              prev.map((f) =>
                f.localId === localId ? { ...f, fileId: String(match.id) } : f
              )
            )
          }
          refetchStatus()
          return
        }

        // Path B: just re-notify with the existing s3Path (processing failed)
        setFiles((prev) =>
          prev.map((f) =>
            f.localId === localId
              ? {
                  ...f,
                  phase: "PROCESSING" as AgentFilePhase,
                  processingPct: 0,
                  error: undefined
                }
              : f
          )
        )
        processingStartedAt.current.set(localId, Date.now())

        const notifyRes = await notifyFilesUploaded({
          tokenInfo: tokenInfo as ITokenInfo,
          sessionId: resolvedSessionId,
          filesPath: [target.s3Path as string],
          showMyFilesInRfpResponse
        })
        const notifyFile = (notifyRes.data?.data?.files ||
          notifyRes.data?.files ||
          [])[0]
        if (notifyFile?.id) {
          setFiles((prev) =>
            prev.map((f) =>
              f.localId === localId
                ? { ...f, fileId: String(notifyFile.id) }
                : f
            )
          )
        }
        refetchStatus()
      } catch (err: any) {
        const message =
          err?.response?.data?.error?.message ||
          err?.response?.data?.message ||
          err?.message ||
          t("rfp_os.label_retry_failed_default")
        fail(message)
      }
    },
    [
      files,
      parentSessionId,
      refetchStatus,
      resolvedSessionId,
      showMyFilesInRfpResponse,
      tokenInfo,
      t
    ]
  )

  const clear = useCallback(() => {
    abortControllers.current.forEach((c) => c.abort())
    abortControllers.current.clear()
    processingStartedAt.current.clear()
    inFlightNames.current.clear()
    setFiles([])
  }, [])

  const mergeCompletedFiles = useCallback((external: AgentFile[]) => {
    setFiles((prev) => {
      const known = new Set(
        prev.map((p) => p.fileId).filter(Boolean) as string[]
      )
      const newOnes = external.filter((e) => e.fileId && !known.has(e.fileId))
      return [...prev, ...newOnes]
    })
  }, [])

  /**
   * Attach picks from "My Files" SelectFilesPicker into the chat session's
   * CHAT_CONTEXT scope so the docs are indexed for ChatDocQnA. Picks
   * arrive already-uploaded with an s3_path and file_id — we skip the
   * UPLOADING phase entirely and start at PROCESSING, then rely on
   * scoped_files_status polling (same path fresh uploads use) to drive
   * the chip to COMPLETED once dpp finishes parse + CHAT_CONTEXT ingest.
   *
   * Why not `mergeCompletedFiles` (the pre-ENG-1249 behaviour)?
   *   Pre-fix the picker called `mergeCompletedFiles` directly with
   *   phase="COMPLETED", which made Send enabled immediately but left
   *   rfp_session_scoped_files empty and the chat OpenSearch indexes
   *   missing the docs. ChatDocQnA's session_id filter returned 0 →
   *   `handoff_to_parent(not_found=true)` → cascade fallback to
   *   placeholder text (see ENG-1298 session 925fb0f4...).
   *
   * Caller: `agent-chat-input/index.tsx` SelectFilesPicker `onSelect`.
   * Media picks with `transcription_status === "pending"` skip this
   * path — transcription completes server-side via the user-scoped
   * pipeline; chat-context indexing isn't needed for them.
   */
  const attachExistingFiles = useCallback(
    async (picked: ISelectedFile[]): Promise<void> => {
      if (!tokenInfo || picked.length === 0) return

      let sid = resolvedSessionId
      if (!sid && ensureSessionId) {
        try {
          sid = await ensureSessionId()
          if (sid) setResolvedSessionId(sid)
        } catch {
          notificationApi.error({
            message: t("rfp_os.label_upload_failed"),
            description: t("rfp_os.label_unable_to_start_session_upload"),
            placement: "topRight"
          })
          return
        }
      }
      if (!sid) {
        notificationApi.error({
          message: t("rfp_os.label_upload_failed"),
          description: t("rfp_os.label_no_session_available"),
          placement: "topRight"
        })
        return
      }

      // Cap enforcement uses the freshly-resolved sid directly. Going via
      // refetchStatus() would replay the hook's stale-closure sessionId
      // (still "" right after ensureSessionId resolves — setResolvedSessionId
      // is async, no re-render yet), producing /session//scoped_files_status
      // and a 404.
      let serverFiles: ScopedFile[] = statusData?.data?.files ?? []
      try {
        const fresh = await getScopedFilesStatus({
          tokenInfo: tokenInfo as ITokenInfo,
          sessionId: sid,
          showMyFilesInRfpResponse
        })
        const freshList = (fresh.data as { data?: { files?: ScopedFile[] } })
          ?.data?.files
        if (freshList) serverFiles = freshList
      } catch {
        // proceed with cached
      }
      const localTrackedIds = new Set(
        files.map((f) => f.fileId).filter((id): id is string => !!id)
      )
      const serverOnlyCount = serverFiles.filter(
        (f) => !localTrackedIds.has(String(f.id))
      ).length
      const existingCount = files.length + serverOnlyCount
      const remainingSlots = Math.max(0, maxFiles - existingCount)
      const trimmed = picked.slice(0, remainingSlots)
      if (picked.length > remainingSlots) {
        notificationApi.warning({
          message: t("rfp_os.label_too_many_files"),
          description:
            remainingSlots === 0
              ? t("rfp_os.label_session_files_cap_reached", {
                  count: existingCount,
                  max: maxFiles
                })
              : t("rfp_os.label_session_only_first_kept", {
                  max: maxFiles,
                  count: remainingSlots
                }),
          placement: "topRight"
        })
      }

      // Dedup against chips already attached this session. Picker passes
      // bare base64; chip.fileId is the composite
      // "${tenant}|${session}|${base64}" from the attach response (or
      // undefined during the in-flight PROCESSING window — in which case
      // chip.pickFileId carries the bare base64 from seed time). Normalize
      // both sides so re-picks land in the Set regardless of phase.
      const alreadyChipFileIds = new Set(
        files
          .map((f) =>
            f.fileId
              ? normalizeFileId(f.fileId)
              : f.pickFileId
                ? f.pickFileId
                : null
          )
          .filter((id): id is string => !!id)
      )
      const deduped = trimmed.filter(
        (p) => !alreadyChipFileIds.has(normalizeFileId(p.file_id))
      )
      if (deduped.length === 0) return

      // Seed PROCESSING chips immediately so the user gets feedback +
      // Send button gates correctly. fileId starts undefined; the attach
      // response fills it with the BE composite id that
      // scoped_files_status polling correlates against.
      //
      // Track each seed's user-scoped file_id (the picker's `p.file_id`)
      // alongside its localId so we can pair the BE response back to the
      // chip deterministically. We can't pair by `s3_path` — the My Files
      // list endpoint dropped s3_path in the lazy-presign refactor
      // (api-getorbitx PR #3397), so picker entries arrive with
      // `s3_path === ""` and the s3_path key on the BE response has no
      // counterpart on the chip to match against.
      const now = Date.now()
      const localIdToPickFileId = new Map<string, string>()
      const seeds: AgentFile[] = deduped.map((p) => {
        const localId = uid()
        localIdToPickFileId.set(localId, p.file_id)
        processingStartedAt.current.set(localId, now)
        return {
          localId,
          name: p.file_name,
          fileId: undefined,
          // Bare base64 surfaced for the picker's currentlyAttached dedup
          // during the in-flight PROCESSING window (before the attach POST
          // returns and sets fileId to the composite).
          pickFileId: normalizeFileId(p.file_id),
          s3Path: p.s3_path,
          source: p.source,
          phase: "PROCESSING" as AgentFilePhase,
          uploadPct: 100,
          processingPct: 0,
          transcription_status: p.transcription_status
        }
      })
      setFiles((prev) => [...prev, ...seeds])

      try {
        const resp = await attachExistingFilesApi({
          tokenInfo: tokenInfo as ITokenInfo,
          sessionId: sid,
          fileIds: deduped.map((p) => p.file_id)
        })
        const returned: AttachedExistingFile[] =
          resp?.data?.data?.files || resp?.data?.files || []

        // Pair BE response → chip by user-scoped file_id. BE's `file_id`
        // is the original rfp_user_scoped_files.file_id (what the picker
        // sent); BE's `id` is the new composite
        // `${tenant}|${session}|${file_id}` that scoped_files_status
        // returns under `id`. Setting chip.fileId to that composite is
        // what unlocks the status useEffect's match.
        //
        // Race-window catch: scoped_files_status polling runs
        // continuously and may have already responded with this row at
        // 100% BEFORE our attach POST returned (common for picks of
        // already-attached-to-other-session files where dpp ingested in
        // milliseconds, or for re-attach where the row was carried over
        // from a prior session). When that race wins, the status
        // useEffect (deps: [statusData]) already ran with chip.fileId
        // still undefined → it short-circuited at `if (!f.fileId) return f`
        // and left the chip PROCESSING. A subsequent refetchStatus()
        // can't recover the chip because React Query's structural
        // sharing keeps the same `data` reference when the response is
        // identical → useEffect doesn't re-fire. Apply the latest
        // status snapshot inline now that we know the BE id so the chip
        // flips COMPLETED in the same render.
        const statusFiles = statusData?.data?.files
        setFiles((prev) =>
          prev.map((f) => {
            const pickFileId = localIdToPickFileId.get(f.localId)
            if (!pickFileId) return f
            const match = returned.find((r) => r.file_id === pickFileId)
            if (!match) return f
            const beId = String(match.id)
            const statusRow = statusFiles?.find((s) => String(s.id) === beId)
            if (statusRow?.percentage_completion === 100) {
              processingStartedAt.current.delete(f.localId)
              return {
                ...f,
                fileId: beId,
                phase: "COMPLETED" as AgentFilePhase,
                processingPct: 100,
                error: undefined,
                rawFile: undefined
              }
            }
            if (statusRow?.error) {
              return {
                ...f,
                fileId: beId,
                phase: "FAILED" as AgentFilePhase,
                error: statusRow.error
              }
            }
            return { ...f, fileId: beId }
          })
        )

        // Invalidate the new sid's query key directly so this works even
        // when the observer is still on the stale "" key for this render.
        queryClient.invalidateQueries({
          queryKey: ["scoped-files-status", sid],
          refetchType: "all"
        })
      } catch (err: any) {
        const message =
          err?.response?.data?.error?.message ||
          err?.response?.data?.message ||
          err?.message ||
          t("rfp_os.label_unable_to_upload_files_try_again")
        notificationApi.error({
          message: t("rfp_os.label_upload_failed"),
          description: message,
          placement: "topRight",
          duration: 8
        })
        setFiles((prev) =>
          prev.map((f) =>
            seeds.some((s) => s.localId === f.localId)
              ? {
                  ...f,
                  phase: "FAILED" as AgentFilePhase,
                  error: message
                }
              : f
          )
        )
      }
    },
    [
      ensureSessionId,
      files,
      maxFiles,
      notificationApi,
      refetchStatus,
      resolvedSessionId,
      statusData,
      tokenInfo,
      t
    ]
  )

  const allDone = useMemo(
    () =>
      files.length === 0
        ? true
        : files.every((f) => f.phase === "COMPLETED" || f.phase === "FAILED"),
    [files]
  )

  const completedFiles = useMemo(
    () => files.filter((f) => f.phase === "COMPLETED"),
    [files]
  )

  return {
    files,
    completedFiles,
    allDone,
    hasActiveFiles,
    isInitializing,
    addFiles,
    mergeCompletedFiles,
    attachExistingFiles,
    removeFile,
    retryFile,
    clear,
    sessionId: resolvedSessionId
  }
}
